import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { AuthService } from '@core/services/auth.service';
import { FACILITY_ID_HEADER_NAME } from '@core/http/facility-id.constants';
import { environment } from '@env/environment';

/** Where a request is bound, because only two destinations get a facility header at all. */
type FacilityDestination = 'manager-api' | 'assistant' | 'elsewhere';

function matchesBase(url: string, base: string | undefined): boolean {
	const trimmed = (base ?? '').replace(/\/$/, '');

	return trimmed !== '' && url.startsWith(trimmed);
}

/**
 * The assistant is tested first, so that a future where the two share an origin resolves to the
 * more specific base rather than to whichever was listed first.
 */
function destinationOf(url: string): FacilityDestination {
	if (matchesBase(url, environment.aiBaseUrl)) {
		return 'assistant';
	}

	return matchesBase(url, environment.apiBaseUrl) ? 'manager-api' : 'elsewhere';
}

/** Reset-password is token-scoped; the emailed link carries no facility id. */
function isFacilityIdExcludedRequest(url: string): boolean {
	return url.includes('/auth/reset-password');
}

/**
 * Adds `X-Facility-ID` to the requests that need one — the Manager API and the assistant service
 * — carrying the trimmed login input verbatim.
 *
 * Both destinations take the same value, and it is never re-encoded here: what staff type at
 * login is already the encoded form the platform publishes, as `platform/facility-header`
 * requires. Encoding it again yields a double-encoded header that both APIs reject.
 *
 * A request that already carries the header is passed through untouched, so a caller that has to
 * send something else still can.
 */
export const facilityIdInterceptor: HttpInterceptorFn = (req, next) => {
	const destination = destinationOf(req.url);

	if (
		destination === 'elsewhere' ||
		req.headers.has(FACILITY_ID_HEADER_NAME) ||
		isFacilityIdExcludedRequest(req.url)
	) {
		return next(req);
	}

	const facilityId = inject(AuthService).facilityId().trim();
	if (!facilityId) {
		return next(req);
	}

	return next(req.clone({ setHeaders: { [FACILITY_ID_HEADER_NAME]: facilityId } }));
};
